import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Player {
  player_id: string;
  player_type: 'supabase' | 'internal';
  firstName?: string;
  lastName?: string;
  username?: string;
  currentStatus?: string;
}

interface BulkPlayerReadinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  players: Player[];
  teamName: string;
  loading?: boolean;
}

export const BulkPlayerReadinessModal: React.FC<BulkPlayerReadinessModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  players,
  teamName,
  loading = false,
}) => {
  const getPlayerName = (player: Player) => {
    if (player.firstName && player.lastName) {
      return `${player.firstName} ${player.lastName}`;
    }
    return player.username || 'Unknown Player';
  };

  const playersWithoutStatus = players.filter(p => !p.currentStatus);
  const playersWithStatus = players.filter(p => p.currentStatus);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark All Players Ready</DialogTitle>
          <DialogDescription>
            You are about to mark {players.length} {teamName} player{players.length !== 1 ? 's' : ''} as "Present - Ready to Play"
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[300px] overflow-y-auto space-y-2 py-4">
          {playersWithoutStatus.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Will be marked ready ({playersWithoutStatus.length}):</p>
              {playersWithoutStatus.map((player) => (
                <div key={player.player_id} className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                  <CheckCircle className="h-4 w-4 text-sports-green" />
                  <span className="text-sm">{getPlayerName(player)}</span>
                </div>
              ))}
            </div>
          )}

          {playersWithStatus.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-sm font-medium">Already have status ({playersWithStatus.length}):</p>
              {playersWithStatus.map((player) => (
                <div key={player.player_id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-md">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{getPlayerName(player)}</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {player.currentStatus}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={loading}>
            {loading ? 'Updating...' : `Confirm - Mark ${playersWithoutStatus.length} Ready`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
